import { differenceInHours, max } from 'date-fns'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { AssignmentStatus } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { ActivityStatus } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/clientFilters/ClientFiltersPopover'

import { ActivityStatusItem } from './columns/ClientListActivityInfo'

type AppointmentRow = {
  lastAppointment?: Appointment | null
  nextAppointment?: Appointment | null
}
export function sortLastAppointmentRow(aRow: AppointmentRow, bRow: AppointmentRow): number {
  const getTime = (date?: Appointment | null) => (date ? getDateFromAppointment(date).getTime() : null)

  const aNext = getTime(aRow.nextAppointment)
  const bNext = getTime(bRow.nextAppointment)
  const aPrev = getTime(aRow.lastAppointment)
  const bPrev = getTime(bRow.lastAppointment)

  // 1. If both have nextAppointment, compare those
  if (aNext !== null && bNext !== null) {
    return aNext - bNext
  }

  // 2. If only one has nextAppointment, it comes first
  if (aNext !== null) return -1
  if (bNext !== null) return 1

  // 3. If both have prevAppointment, compare in reverse (recent last)
  if (aPrev !== null && bPrev !== null) {
    return bPrev - aPrev
  }

  // 4. If only one has prevAppointment, it comes next
  if (aPrev !== null) return -1
  if (bPrev !== null) return 1

  // 5. Both have no appointments
  return 0
}

export function getActivityStatus(
  assignmentStatuses: AssignmentStatus[] | undefined | null,
  nextAppointment: Appointment | undefined | null,
): ActivityStatusItem | undefined {
  if (assignmentStatuses === undefined || nextAppointment === undefined) {
    return undefined
  }

  if (nextAppointment === null || assignmentStatuses === null) {
    return {
      status: null,
      sendDate: null,
    }
  }

  const assignmentStatusesForNextAppointment = assignmentStatuses.filter(
    (assignment) =>
      assignment.session_period === nextAppointment.sessionNumber ||
      (nextAppointment.sessionNumber === 1 && assignment.session_period <= 1),
  )
  if (assignmentStatusesForNextAppointment.length === 0) {
    return {
      status: ActivityStatus.NOT_SENT,
      sendDate: null,
    }
  }
  const sendDate = max(assignmentStatusesForNextAppointment.map((assignment) => new Date(assignment.create_date)))
  const allComplete = assignmentStatusesForNextAppointment.every((assignment) => assignment.status === 'completed')
  if (allComplete) {
    return {
      status: ActivityStatus.COMPLETE,
      sendDate,
    }
  }
  const appointmentDate = getDateFromAppointment(nextAppointment)
  const isAppointmentWithin48Hours = differenceInHours(appointmentDate, new Date()) <= 48
  return {
    status: isAppointmentWithin48Hours ? ActivityStatus.INCOMPLETE : ActivityStatus.PENDING,
    sendDate,
  }
}

export function filterBy<TEnum extends Record<string, string>>(
  filters: (keyof TEnum)[],
  filterFns: Record<keyof TEnum, () => boolean>,
): boolean {
  return filters.some((filter) => filterFns[filter]())
}
